import React, { useState, useEffect } from 'react';
import { api } from '../config/api';
import toast from 'react-hot-toast';
import * as ps from '../services/pocketsmith.service';
import { tw, monoFont } from '../utils/terminalTheme';

interface Subscription {
  id: number;
  site_name: string;
  monthly_price: number;
  billing_cycle: 'Monthly' | 'Quarterly' | 'Yearly';
  description: string;
  url: string;
  category: string;
  created_at: string;
  updated_at: string;
}

interface Summary {
  category: string;
  subscription_count: number;
  total_monthly_cost: number;
  total_yearly_cost: number;
}

export const Subscriptions: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [summary, setSummary] = useState<Summary[]>([]);
  const [totalMonthly, setTotalMonthly] = useState(0);
  const [totalYearly, setTotalYearly] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [reconData, setReconData] = useState<ps.SubscriptionCheck[]>([]);
  const [reconIssues, setReconIssues] = useState(0);
  const [showRecon, setShowRecon] = useState(false);

  const [formData, setFormData] = useState({
    site_name: '',
    monthly_price: '',
    billing_cycle: 'Monthly' as 'Monthly' | 'Quarterly' | 'Yearly',
    description: '',
    url: '',
    category: ''
  });

  useEffect(() => {
    fetchSubscriptions();
    fetchReconciliation();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const [subsRes, summaryRes, totalsRes] = await Promise.all([
        api.get('/subscriptions/'),
        api.get('/subscriptions/summary'),
        api.get('/subscriptions/totals')
      ]);

      setSubscriptions(subsRes.data);
      setSummary(summaryRes.data);
      setTotalMonthly(totalsRes.data.total_monthly || 0);
      setTotalYearly(totalsRes.data.total_yearly || 0);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchReconciliation = async () => {
    try {
      const result = await ps.getSubscriptionCheck(3);
      if (result.success) {
        setReconData(result.data);
        setReconIssues(result.issues);
      }
    } catch (err) {
      // PocketSmith reconciliation is optional
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        monthly_price: parseFloat(formData.monthly_price)
      };

      if (editingId) {
        await api.put(`/subscriptions/${editingId}`, data);
        toast.success('Subscription updated');
      } else {
        await api.post('/subscriptions/', data);
        toast.success('Subscription added');
      }

      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchSubscriptions();
    } catch (error) {
      console.error('Error saving subscription:', error);
      toast.error('Failed to save subscription');
    }
  };

  const handleEdit = (sub: Subscription) => {
    setFormData({
      site_name: sub.site_name,
      monthly_price: sub.monthly_price.toString(),
      billing_cycle: sub.billing_cycle,
      description: sub.description,
      url: sub.url,
      category: sub.category
    });
    setEditingId(sub.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Remove this subscription?')) return;
    try {
      await api.delete(`/subscriptions/${id}`);
      toast.success('Subscription deleted');
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to delete subscription');
    }
  };

  const resetForm = () => {
    setFormData({
      site_name: '',
      monthly_price: '',
      billing_cycle: 'Monthly',
      description: '',
      url: '',
      category: ''
    });
  };

  if (loading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  return (
    <div className={tw.page}>
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white" style={monoFont}>
            <span className="text-cyan-400">~/</span>subscriptions
          </h1>
          <p className="text-gray-500 text-sm font-mono mt-1">
            <span className="text-green-500">$</span> tracking {subscriptions.length} services
            <span className="text-gray-600 mx-2">|</span>
            burn rate: <span className="text-cyan-400">${totalMonthly.toFixed(2)}</span>/mo
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (showForm) { setEditingId(null); resetForm(); }
          }}
          className={tw.btnPrimary}
        >
          {showForm ? '[ cancel ]' : '+ add subscription'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Monthly Burn</div>
          <div className={tw.statValueCyan}>${totalMonthly.toFixed(2)}</div>
        </div>
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Annual Cost</div>
          <div className={`${tw.statValue} text-white`}>${totalYearly.toFixed(2)}</div>
        </div>
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Services</div>
          <div className={`${tw.statValue} text-white`}>{subscriptions.length}</div>
        </div>
      </div>

      {/* Reconciliation Panel */}
      {reconData.length > 0 && (
        <div className="mb-8">
          <button
            onClick={() => setShowRecon(!showRecon)}
            className={`w-full text-left rounded-lg p-4 border transition-colors ${
              reconIssues > 0
                ? 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/15'
                : 'bg-gray-800/80 border-gray-700/50 hover:bg-gray-800'
            }`}
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-lg">{reconIssues > 0 ? '\u26A0' : '\u2705'}</span>
                <span className="text-white font-mono text-sm">PocketSmith Reconciliation</span>
                {reconIssues > 0 && (
                  <span className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs px-2 py-0.5 rounded-full font-mono">
                    {reconIssues} issue{reconIssues !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <span className="text-gray-500 text-xs font-mono">{showRecon ? 'hide' : 'show'} details</span>
            </div>
          </button>

          {showRecon && (
            <div className={`${tw.tableWrap} rounded-t-none border-t-0`}>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700/50">
                    <th className={tw.tableHeader}>Service</th>
                    <th className={tw.tableHeaderRight}>Expected</th>
                    <th className={tw.tableHeaderRight}>Last Charge</th>
                    <th className="px-5 py-3 text-center text-xs font-mono text-gray-500 uppercase tracking-wider">Matches</th>
                    <th className={tw.tableHeader}>Last Date</th>
                    <th className="px-5 py-3 text-center text-xs font-mono text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className={tw.tableDivider}>
                  {reconData.map(r => {
                    const statusMap: Record<string, { bg: string; text: string; border: string; label: string }> = {
                      matched:        { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Matched' },
                      price_changed:  { bg: 'bg-yellow-500/15',  text: 'text-yellow-400',  border: 'border-yellow-500/30',  label: 'Price Changed' },
                      amount_mismatch:{ bg: 'bg-orange-500/15',  text: 'text-orange-400',  border: 'border-orange-500/30',  label: 'Mismatch' },
                      overdue:        { bg: 'bg-red-500/15',     text: 'text-red-400',     border: 'border-red-500/30',     label: 'Overdue' },
                      no_match:       { bg: 'bg-gray-500/15',    text: 'text-gray-400',    border: 'border-gray-500/30',    label: 'Not Found' },
                    };
                    const sc = statusMap[r.status] || statusMap.no_match;
                    return (
                      <tr key={r.subscription_id} className={tw.tableRow}>
                        <td className={tw.tableCell}>
                          <div className="text-white text-sm font-mono">{r.site_name}</div>
                          <div className="text-gray-600 text-xs font-mono">{r.category} &middot; {r.billing_cycle}</div>
                        </td>
                        <td className={tw.tableCellRight}>
                          <span className="text-cyan-400 font-mono text-sm">${r.expected_monthly.toFixed(2)}</span>
                          <span className="text-gray-600 font-mono text-xs">/mo</span>
                        </td>
                        <td className={tw.tableCellRight}>
                          {r.last_charge_amount > 0 ? (
                            <span className={`font-mono text-sm ${r.status === 'matched' ? 'text-emerald-400' : r.status === 'price_changed' || r.status === 'amount_mismatch' ? 'text-yellow-400' : 'text-gray-400'}`}>
                              ${r.last_charge_amount.toFixed(2)}
                            </span>
                          ) : (
                            <span className="text-gray-600 font-mono text-sm">--</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-center text-gray-400 text-sm font-mono">{r.matched_count}</td>
                        <td className={tw.tableCell}>
                          <span className="text-gray-400 text-sm font-mono">{r.last_charge_date || '--'}</span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-mono ${sc.bg} ${sc.text} border ${sc.border}`}>
                            {sc.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className={`${tw.formWrap} mb-8`}>
          <h2 className={tw.formTitle}>
            {editingId ? '> edit subscription' : '> add new subscription'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className={tw.label}>service_name *</label>
                <input type="text" required value={formData.site_name}
                  onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
                  placeholder="e.g., Netflix" className={tw.input} />
              </div>
              <div>
                <label className={tw.label}>category *</label>
                <input type="text" required value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., Streaming, Insurance" className={tw.input} />
              </div>
              <div>
                <label className={tw.label}>monthly_price *</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 font-mono text-sm">$</span>
                  <input type="number" step="0.01" required value={formData.monthly_price}
                    onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
                    className={`${tw.input} pl-7`} />
                </div>
              </div>
              <div>
                <label className={tw.label}>billing_cycle *</label>
                <select required value={formData.billing_cycle}
                  onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value as any })}
                  className={tw.select}>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className={tw.label}>url</label>
                <input type="url" value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://..." className={tw.input} />
              </div>
              <div>
                <label className={tw.label}>description</label>
                <input type="text" value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What this is for" className={tw.input} />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className={tw.btnPrimary}>
                {editingId ? 'update' : 'add'} subscription
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); resetForm(); }}
                className={tw.btnSecondary}>cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Category Summary */}
      {summary.length > 0 && (
        <div className={`${tw.tableWrap} p-6 mb-8`}>
          <h2 className="text-lg font-mono text-white mb-4">
            <span className="text-gray-500">by_category</span>
          </h2>
          <div className="space-y-3">
            {summary.map((cat) => (
              <div key={cat.category} className="flex justify-between items-center border-b border-gray-700/30 pb-2">
                <div>
                  <span className="text-white font-mono text-sm">{cat.category}</span>
                  <span className="text-gray-600 text-xs font-mono ml-2">({cat.subscription_count})</span>
                </div>
                <div className="text-right">
                  <span className="text-cyan-400 font-mono text-sm font-medium">${cat.total_monthly_cost.toFixed(2)}</span>
                  <span className="text-gray-600 font-mono text-xs">/mo</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscriptions Table */}
      <div className={tw.tableWrap}>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700/50">
              <th className={tw.tableHeader}>Service</th>
              <th className={tw.tableHeader}>Category</th>
              <th className={tw.tableHeaderRight}>Cost</th>
              <th className={tw.tableHeader}>Cycle</th>
              <th className={`${tw.tableHeader} hidden lg:table-cell`}>Description</th>
              <th className={tw.tableHeaderRight}>Actions</th>
            </tr>
          </thead>
          <tbody className={tw.tableDivider}>
            {subscriptions.map((sub) => (
              <tr key={sub.id} className={tw.tableRow}>
                <td className={tw.tableCell}>
                  {sub.url ? (
                    <a href={sub.url} target="_blank" rel="noopener noreferrer"
                      className="text-white hover:text-cyan-400 font-medium text-sm transition-colors">
                      {sub.site_name}
                      <span className={`text-gray-600 ml-1 text-xs ${tw.hoverReveal}`}>&nearr;</span>
                    </a>
                  ) : (
                    <span className="text-white font-medium text-sm">{sub.site_name}</span>
                  )}
                </td>
                <td className={tw.tableCell}>
                  <span className="text-gray-400 text-xs font-mono">{sub.category}</span>
                </td>
                <td className={tw.tableCellRight}>
                  <span className="text-cyan-400 font-mono text-sm font-medium">${sub.monthly_price.toFixed(2)}</span>
                  <span className="text-gray-600 font-mono text-xs">/mo</span>
                </td>
                <td className={tw.tableCell}>
                  <span className="text-gray-400 text-xs font-mono">{sub.billing_cycle}</span>
                </td>
                <td className={`${tw.tableCell} hidden lg:table-cell`}>
                  <span className="text-gray-500 text-xs">{sub.description || ''}</span>
                </td>
                <td className={tw.tableCellRight}>
                  <div className={`flex gap-3 justify-end ${tw.hoverReveal}`}>
                    <button onClick={() => handleEdit(sub)} className={tw.btnGhost}>edit</button>
                    <button onClick={() => handleDelete(sub.id)} className={tw.btnDanger}>rm</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {subscriptions.length === 0 && (
          <div className={tw.empty}>no subscriptions yet — click "+ add subscription" to get started</div>
        )}
        {subscriptions.length > 0 && (
          <div className={tw.tableFooter}>
            <span className="text-gray-500 text-xs font-mono">{subscriptions.length} service{subscriptions.length !== 1 ? 's' : ''}</span>
            <span className="font-mono text-sm">
              <span className="text-gray-500">total: </span>
              <span className="text-cyan-400 font-medium">${subscriptions.reduce((sum, s) => sum + s.monthly_price, 0).toFixed(2)}</span>
              <span className="text-gray-600">/mo</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
